// Libs
import classNames from 'classnames/bind';
import { ChangeEvent, HTMLInputTypeAttribute, useState } from 'react';
import { Box, IconButton, InputAdornment, InputLabel, Stack, TextField } from '@mui/material';
import { useTranslation } from 'react-i18next';
// Components, Layouts, Pages
import { BaseLabel } from '~/components/common';
// Others
import { EMPTY_STRING } from '~/utils/constants/common';
import { TLabelStyleType } from '~/utils/type/common';
// Styles, images, icons
import styles from './BaseInput.module.scss';
import { icons } from '~/assets';

type Props = {
  iconLabel?: string;
  label?: string;
  gap?: number | string;
  styleTypeLabel?: TLabelStyleType;
  name?: string;
  width?: number | string;
  height?: number | string;
  borderRadius?: number | string;
  isBorder?: boolean;
  min?: number;
  max?: number;
  textAlignCenter?: boolean;
  type?: HTMLInputTypeAttribute;
  value?: string;
  required?: boolean;
  step?: number;
  disabled?: boolean;
  placeholder?: string;
  readOnly?: boolean;
  errorMessage?: string;
  multiline?: boolean;
  rows?: number;
  childrenLeft?: React.ReactNode;
  startAdornment?: React.ReactNode;
  childrenRight?: React.ReactNode;
  onChange?: (event: ChangeEvent<HTMLInputElement>) => void;
  onBlur?: () => void;
};

const cx = classNames.bind(styles);

const BaseInput = (props: Props) => {
  //#region Destructuring Props
  const {
    label,
    iconLabel,
    name,
    width,
    gap,
    height = 36,
    type,
    value,
    textAlignCenter,
    placeholder,
    styleTypeLabel = 'input',
    borderRadius = '27px',
    isBorder = true,
    required = false,
    disabled = false,
    readOnly,
    multiline,
    rows,
    errorMessage,
    startAdornment,
    step,
    min,
    max,
    childrenLeft,
    childrenRight,
    onChange,
    onBlur,
  } = props;
  //#endregion Destructuring Props

  //#region Declare Hook
  const { t } = useTranslation(['common/base']);
  //#endregion Declare Hook

  //#region Selector
  //#endregion Selector

  //#region Declare State
  const [isShowPassword, setIsShowPassword] = useState<boolean>(false);
  //#endregion Declare State

  //#region Declare Memo
  //#endregion Declare Memo

  //#region Implement Hook
  //#endregion Implement Hook

  //#region Handle Function
  const handleShowPassword = () => {
    setIsShowPassword((show) => !show);
  };

  const handleMouseDownPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };

  const handleMouseUpPassword = (event: React.MouseEvent<HTMLButtonElement>) => {
    event.preventDefault();
  };
  //#endregion Handle Function

  return (
    <Box component={'div'} id='baseInputComponent' className={cx('inputContainer')}>
      <Stack direction={'row'} gap={2} width={'100%'} alignItems={'center'}>
        {childrenLeft}

        <Stack direction={'column'} width={'100%'} className={cx('content')} gap={gap}>
          {label && <BaseLabel icon={iconLabel} label={label} required={required} styleType={styleTypeLabel} />}

          {type === 'password' ? (
            <TextField
              fullWidth
              focused={false}
              sx={{ width }}
              type={isShowPassword ? 'text' : 'password'}
              slotProps={{
                input: {
                  className: cx('textFieldInput', { disabled: disabled }),
                  sx: { height, borderRadius },
                  readOnly,
                  endAdornment: (
                    <InputAdornment position='end'>
                      <IconButton
                        onClick={handleShowPassword}
                        onMouseDown={handleMouseDownPassword}
                        onMouseUp={handleMouseUpPassword}
                        edge='end'
                        className={cx('passwordButton')}
                      >
                        {isShowPassword ? (
                          <img src={icons.commonIconEyeShow} alt={t('common_alt_img')} className={cx('passwordIcon')} />
                        ) : (
                          <img
                            src={icons.commonIconEyeHidden}
                            alt={t('common_alt_img')}
                            className={cx('passwordIcon')}
                          />
                        )}
                      </IconButton>
                    </InputAdornment>
                  ),
                },
                formHelperText: { className: cx('errorMessage') },
              }}
              name={name}
              value={value || EMPTY_STRING}
              placeholder={placeholder}
              disabled={disabled}
              onChange={onChange}
              error={!!errorMessage}
              helperText={errorMessage}
            />
          ) : (
            <TextField
              fullWidth
              sx={{ width }}
              type={type}
              slotProps={{
                input: {
                  className: cx('textFieldInput', {
                    disabled,
                    multiline,
                    isBorder,
                  }),
                  inputProps: {
                    style: {
                      textAlign: textAlignCenter ? 'center' : 'left',
                    },
                    step,
                    min,
                    max,
                  },
                  sx: {
                    height,
                    borderRadius,
                    ...(!isBorder ? { borderWidth: 0 } : {}),
                  },
                  readOnly,
                  startAdornment: startAdornment,
                },
                formHelperText: { className: cx('errorMessage') },
              }}
              placeholder={placeholder}
              name={name}
              onBlur={onBlur}
              value={value || EMPTY_STRING}
              disabled={disabled}
              multiline={multiline}
              rows={rows}
              onChange={onChange}
              error={!!errorMessage}
              helperText={errorMessage}
            />
          )}
        </Stack>

        {childrenRight}
      </Stack>
    </Box>
  );
};

export default BaseInput;
